import { useContext, useMemo, useEffect, useState } from "react";
import UIkit from "uikit";
import { InputContainer } from "uikit-react";
import { Form, FormInputWrapper } from "../../ui";
import { useForm } from "react-hook-form";
import { AppContexts } from "../../providers";
import Preview from "../settings/GuestBook/preview";
import { postPreviewSettings } from "./guide_preview/message";

const guestbook_defaults = {
  account_id: 0,
  tab_order: "arrival,about,departure,rules,safety",
  guestlink_expiration: 0,
  guestlink_available: 0,
  guestbook_headline: "",
  attachments: [],
};

const max_welcome_msg_length = 144;

const key_maps = {
  none: "Disabled",
  optional: "Enabled",
};

const mergeGuestbookAttachments = (...attachmentLists) => {
  const merged = [];

  attachmentLists.flat().forEach((attachment) => {
    if (!attachment) {
      return;
    }

    const key = attachment.id || `${attachment.type}:${attachment.url || ""}`;
    if (!merged.find((item) => (item.id || `${item.type}:${item.url || ""}`) === key)) {
      merged.push(attachment);
    }
  });

  return merged;
};

const BuilderGuestbookPanel = ({ section = "guestbook", onSaved }) => {
  const { set, settings, loading, get } = useContext(
    AppContexts.SettingsContext,
  );

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    formState: { isDirty },
  } = useForm();

  const [dirty, setDirty] = useState(false);
  const [capture, setCapture] = useState(false);

  // Ensure guestbook settings are loaded when this panel mounts
  useEffect(() => {
    if (!settings?.guestbook) {
      get(["guestbook"]);
    }
  }, [settings, get]);

  const guestbook = useMemo(() => {
    if (settings?.guestbook) {
      return settings.guestbook;
    }
    return guestbook_defaults;
  }, [settings]);

  const allGuestbookAttachments = useMemo(
    () =>
      mergeGuestbookAttachments(
        settings?.guestbook?.attachments || [],
        settings?.branding?.attachments || [],
      ),
    [settings?.branding?.attachments, settings?.guestbook?.attachments],
  );

  useEffect(() => {
    // hydrate form values from guestbook
    if (guestbook) {
      setValue("guestlink_available", guestbook.guestlink_available);
      setValue("guestlink_expiration", guestbook.guestlink_expiration);
      setValue("email_capture", guestbook.email_capture || "none");
      setValue("email_capture_title", guestbook.email_capture_title || "");
      setValue("email_capture_intro", guestbook.email_capture_intro || "");
      setValue(
        "tab_order",
        guestbook.tab_order || guestbook_defaults.tab_order,
      );
    }
  }, [guestbook, setValue]);

  // toggle capture UI
  useEffect(() => {
    const ec = getValues("email_capture");
    if (ec === "none") {
      setCapture(false);
    } else if (ec === "optional" || ec === "required") {
      setCapture(true);
    }
  }, [watch("email_capture")]);

  const guestlink_available = useMemo(
    () => getValues("guestlink_available"),
    [watch("guestlink_available")],
  );
  const guestlink_expiration = useMemo(
    () => getValues("guestlink_expiration"),
    [watch("guestlink_expiration")],
  );

  const handleUpdate = async (data) => {
    let attachment_buf = allGuestbookAttachments;

    const updateData = {
      ...guestbook_defaults,
      ...guestbook, // keep any fields we don't know about
      guestlink_available:
        data.guestlink_available !== undefined
          ? parseInt(data.guestlink_available, 10)
          : parseInt(guestbook.guestlink_available ?? 0, 10),
      guestlink_expiration:
        data.guestlink_expiration !== undefined
          ? parseInt(data.guestlink_expiration, 10)
          : parseInt(guestbook.guestlink_expiration ?? 0, 10),
      email_capture:
        data.email_capture !== undefined
          ? data.email_capture || "none"
          : guestbook.email_capture || "none",
      email_capture_title:
        data.email_capture_title !== undefined
          ? data.email_capture_title || ""
          : guestbook.email_capture_title || "",
      email_capture_intro:
        data.email_capture_intro !== undefined
          ? data.email_capture_intro || ""
          : guestbook.email_capture_intro || "",
      attachments: attachment_buf,
      tab_order:
        data.tab_order !== undefined
          ? data.tab_order || guestbook_defaults.tab_order
          : guestbook.tab_order || guestbook_defaults.tab_order,
    };

    try {
      await set("guestbook", updateData);
      postPreviewSettings(updateData);
      reset({}, { keepValues: true });
      setDirty(false);
      onSaved && onSaved();
    } catch {
      UIkit.notification("Unable to save guide settings", "error");
    }
  };

  const availableNames = {
    0: "Always Available",
  };

  const expirationNames = {
    0: "No Expiration",
  };

  const charsRemaining =
    max_welcome_msg_length - (getValues("email_capture_intro")?.length || 0);

  const showGuestbookSettings = section === "guestbook";
  const showSubscriptionPopup = section === "subscription-popup";

  return (
    <div>
      <p className="uk-text-meta">
        {showSubscriptionPopup
          ? "Control the opt-in popup shown to guestbook visitors."
          : "Set the availability and expiration of the guestbook link."}
      </p>
      <Form
        className="uk-grid-small"
        data-uk-grid
        onSubmit={handleSubmit(handleUpdate)}
      >
        {showGuestbookSettings && (
          <>
        <div className="uk-width-1-1">
          <FormInputWrapper
            label={
              <span className="uk-text-nowrap">
                Guestbook Link Available From
              </span>
            }
          >
            <InputContainer>
              <div uk-form-custom="target: > * > span:first-child">
                <select
                  {...register("guestlink_available")}
                  onInput={() => setDirty(true)}
                >
                  {[...Array(30)].map((_, i) => (
                    <option value={i} key={i}>
                      {availableNames[i] || `${i} Days Before Check-In`}
                    </option>
                  ))}
                </select>
                <button
                  className="uk-button uk-button-default"
                  type="button"
                  tabIndex="-1"
                >
                  <span>
                    {availableNames[guestlink_available] ||
                      `${guestlink_available} Days Before Check-In`}
                  </span>
                  <span
                    uk-icon="icon: chevron-down"
                    className="hms-align-right"
                  ></span>
                </button>
              </div>
            </InputContainer>
          </FormInputWrapper>
        </div>

        {/* Expires At */}
        <div className="uk-width-1-1">
          <FormInputWrapper label="Expires After Checkout">
            <InputContainer>
              <div uk-form-custom="target: > * > span:first-child">
                <select
                  {...register("guestlink_expiration")}
                  onInput={() => setDirty(true)}
                >
                  {[...Array(11)].map((_, i) => (
                    <option value={i} key={i}>
                      {expirationNames[i] || `${i} Days After Check-Out`}
                    </option>
                  ))}
                </select>
                <button
                  className="uk-button uk-button-default"
                  type="button"
                  tabIndex="-1"
                >
                  <span>
                    {expirationNames[guestlink_expiration] ||
                      `${guestlink_expiration} Days After Check-Out`}
                  </span>
                  <span
                    uk-icon="icon: chevron-down"
                    className="hms-align-right"
                  ></span>
                </button>
              </div>
            </InputContainer>
          </FormInputWrapper>
        </div>

        </>
        )}

        {/* Subscription */}
        {showSubscriptionPopup && (
        <div className="uk-width-1-1">
          <div className="uk-form-label">
            Subscription
            <img
              className="info-icon"
              src="/images/info-icon.svg"
              alt="info"
              uk-tooltip="Collect email addresses from anyone viewing the guestbooks"
            />
          </div>
          <input type="hidden" {...register("email_capture")} />
          <div className="uk-form-controls guide-designer-toggle-row">
            <span className="secondary">
              {capture ? "Enabled" : "Disabled"}
            </span>
            <div className="autosend-checkbox">
              <label
                htmlFor="guideDesignerSubscriptionToggle"
                className="setting-switch"
              >
                <input
                  type="checkbox"
                  id="guideDesignerSubscriptionToggle"
                  checked={capture}
                  onChange={(event) => {
                    const nextValue = event.target.checked;
                    setCapture(nextValue);
                    setValue(
                      "email_capture",
                      nextValue ? "optional" : "none",
                    );
                    setDirty(true);
                  }}
                />
                <span className="setting-slider setting-round"></span>
              </label>
            </div>
          </div>
        </div>
        )}

        {showSubscriptionPopup && capture && (
          <>
            <div className="uk-width-1-1">
              <FormInputWrapper label="Subscription Popup Title" size="two">
                <InputContainer>
                  <input
                    className="uk-input"
                    type="text"
                    placeholder="Subscribe to our Newsletter"
                    onInput={() => setDirty(true)}
                    {...register("email_capture_title")}
                  />
                </InputContainer>
              </FormInputWrapper>
            </div>

            <div className="uk-width-1-1">
              <div className="uk-form-label">Subscription Popup Intro Text</div>
              <div className="uk-form-controls">
                <textarea
                  className="uk-textarea"
                  placeholder="Sign up to receive exclusive offers for your next stay."
                  rows="3"
                  onInput={() => setDirty(true)}
                  {...register("email_capture_intro")}
                ></textarea>
              </div>
              <p className="secondary">
                Characters Remaining: {charsRemaining}
              </p>
            </div>

            <div className="uk-width-1-1">
              <button
                type="button"
                className="uk-button hms-btn hms-white-btn"
                uk-toggle="target: #newsletter"
              >
                Preview Popup
              </button>
            </div>
          </>
        )}

        {/* Save */}
        <div className="uk-width-1-1 uk-margin-top">
          <div className="uk-flex uk-flex-middle">
            <button
              className="uk-button hms-btn hms-red-btn"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <div data-uk-spinner="ratio: 0.5;"></div>
              ) : dirty || isDirty ? (
                "Save"
              ) : (
                "Saved"
              )}
            </button>
          </div>
        </div>
      </Form>

      <Preview
        title={getValues("email_capture_title")}
        intro={getValues("email_capture_intro")}
      />
    </div>
  );
};

export default BuilderGuestbookPanel;
